import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, FormGroup, FormControlLabel, Divider } from "@mui/material";
import isEmailValidator from "validator/lib/isEmail";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryCheckbox from "../../Inputs/PrimaryCheckBox/PrimaryCheckBox";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import StandAloneRadio from "../../Buttons/StandaloneRadio/StandAloneRadio";
import { handleAltEnter } from "../../../utils/keyboardShortcutHelpers";
import { parsePhoneNumber, formatPhoneNumber } from "../../../utils/formatHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function CarrierContactsModal() {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  let [searchParams] = useSearchParams();
  const [action, setAction] = useState("");
  const [label, setLabel] = useState("");
  const [carrierId, setCarrierId] = useState("");
  const [contact, setContact] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneExt, setPhoneExt] = useState("");
  const [mobile, setMobile] = useState("");
  const [mobileExt, setMobileExt] = useState("");
  const [email, setEmail] = useState("");
  const [fax, setFax] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [isDispatch, setIsDispatch] = useState(false);
  const [isDriver, setIsDriver] = useState(false);
  const [isBilling, setIsBilling] = useState(false);
  const [isOsd, setIsOsd] = useState(false);
  const [isAfterhours, setIsAfterhours] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isOther, setIsOther] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [contactTypeValid, setContactTypeValid] = useState(false);
  const [nameValid, setNameValid] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [disablePrimaryPhone, setDisablePrimaryPhone] = useState(false);

  const roles = [
    {
      label: "Primary Contact",
      value: "primary",
      state: isPrimary
    },
    {
      label: "Dispatch",
      value: "dispatch",
      state: isDispatch
    },
    {
      label: "Billing",
      value: "billing",
      state: isBilling
    },
    {
      label: "OS&D",
      value: "osd",
      state: isOsd
    },
    {
      label: "After Hours",
      value: "after-hours",
      state: isAfterhours
    },
    {
      label: "Driver",
      value: "driver",
      state: isDriver
    },
    {
      label: "Owner",
      value: "owner",
      state: isOwner
    },
    {
      label: "Other",
      value: "other",
      state: isOther
    }
  ];

  const setInitialValues = () => {
    setName(contact.name);
    setPhone(formatPhoneNumber(parsePhoneNumber(contact.phone, "phone")));
    setPhoneExt(parsePhoneNumber(contact.phone, "ext"));
    setMobile(formatPhoneNumber(parsePhoneNumber(contact.mobile, "phone")));
    setMobileExt(parsePhoneNumber(contact.mobile, "ext"));
    setEmail(contact.email);
    setFax(formatPhoneNumber(contact.fax));
    setIsPrimary(contact.isPrimary);
    setIsDispatch(contact.isDispatch);
    setIsDriver(contact.isDriver);
    setIsBilling(contact.isBilling);
    setIsOsd(contact.isOsd);
    setIsAfterhours(contact.isAfterhours);
    setPrimaryPhone(contact.primaryPhone);
    setIsOwner(contact.isOwner);
    setIsOther(contact.isOther);
  };

  const handleCancel = () => {
    refreshEmailValid();
    handleReset();
    handleClose();
  };

  const handleClose = () => {
    window.close();
  };

  const handleReset = () => {
    if (!contact) {
      setName("");
      setPhone("");
      setPhoneExt("");
      setMobile("");
      setMobileExt("");
      setEmail("");
      setFax("");
      setIsPrimary(false);
      setIsDispatch(false);
      setIsDriver(false);
      setIsBilling(false);
      setIsOsd(false);
      setIsAfterhours(false);
      setPrimaryPhone("");
      setIsOwner(false);
      setIsOther(false);
    } else {
      setInitialValues();
    }
  };

  const handleSubmit = () => {
    setIsUpdating(true);
    setSubmitDisabled(true);
    let updatedContact = {
      id: contact ? contact.id : undefined,
      name: name,
      phone: concatPhone(phone, phoneExt),
      mobile: concatPhone(mobile, mobileExt),
      primaryPhone: primaryPhone,
      email: email,
      fax: fax,
      isDispatch: isDispatch,
      isBilling: isBilling,
      isOsd: isOsd,
      isAfterhours: isAfterhours,
      isDriver: isDriver,
      isPrimary: isPrimary,
      isOwner: isOwner,
      isOther: isOther,
      carrierId: carrierId,
      contactAssociation: "P",
      contactType: "contact"
    };

    if (action === "add-contact") {
      CarrierService.CreateCarrierContact(instance, updatedContact).then(() => {
        setIsUpdating(false);
        setSubmitDisabled(false);
        handleClose();
      }).catch(error => {
        console.error(error);
        setIsUpdating(false);
        setSubmitDisabled(false);
        handleClose();
        warning(error);
      });
    } else if (action === "edit-contact") {
      CarrierService.UpdateCarrierContact(instance, updatedContact).then(res => {
        res.isUpdate = true;
        setIsUpdating(false);
        setSubmitDisabled(false);
        handleClose();
      }).catch(error => {
        console.error(error);
        setIsUpdating(false);
        setSubmitDisabled(false);
        handleClose();
        warning(error);
      });
    }
  };

  const handleRoleSelect = event => {
    const name = event.currentTarget.value;
    const value = event.target.checked;

    switch (name) {
      case "primary":
        setIsPrimary(value);
        break;
      case "dispatch":
        setIsDispatch(value);
        break;
      case "driver":
        setIsDriver(value);
        break;
      case "billing":
        setIsBilling(value);
        break;
      case "osd":
        setIsOsd(value);
        break;
      case "after-hours":
        setIsAfterhours(value);
        break;
      case "owner":
        setIsOwner(value);
        break;
      case "other":
        setIsOther(value);
        break;
      default:
        break;
    }
  };

  const handlePrimaryPhoneChange = event => {
    setPrimaryPhone(event.target.value);
  };

  const concatPhone = (phone, ext) => {
    if (ext?.length) {
      return `${phone}x${ext}`;
    } else {
      return phone;
    }
  };

  const validatePhone = (number) => {
    if (number) {
      return parseInt(number.length) >= 12;
    } else {
      return null;
    }
  };

  const errorEmailValid = () => {
    return !contactTypeValid ? (validatePhone(phone) || validatePhone(mobile)) ? !emailValid : true : (email == "" || email === null) ? false : !emailValid;
  };

  const refreshEmailValid = () => {
    if (action === "add-contact") setEmailValid(isEmailValidator(email));
    if (action === "edit-contact") setEmailValid(contact.email != null ? isEmailValidator(contact.email) : true);
  };

  useEffect(() => {
    setContactTypeValid(validatePhone(phone) || validatePhone(mobile) || email);
    setNameValid(!!name);
  }, [name, phone, mobile, email]);

  useEffect(() => {
    if (!validatePhone(phone) || !validatePhone(mobile)) {
      setPrimaryPhone("");
      setDisablePrimaryPhone(true);
    } else {
      setDisablePrimaryPhone(false);
    }
  }, [phone, mobile]);

  useEffect(() => {
    setSubmitDisabled(!contactTypeValid || !nameValid || errorEmailValid());
  }, [contactTypeValid, nameValid, emailValid, email]);

  useEffect(() => {
    if (contact) {
      setInitialValues();
    }
  }, [contact]);

  useEffect(() => {
    if (searchParams.get("carrierId")) {
      setCarrierId(searchParams.get("carrierId"));
    }

    const actionParam = searchParams.get("action");
    if (actionParam) {
      setAction(actionParam);

      switch (actionParam) {
        case "add-contact":
          setLabel("Add Carrier Contact");
          break;
        case "edit-contact":
          setLabel("Update Carrier Contact");
          setContact(JSON.parse(localStorage.getItem("carrier-contact")));
          break;
      }
    }
  }, []);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, submitDisabled, handleSubmit)} sx={{ margin: "10px" }}>
      <Box sx={{ marginBottom: "15px" }}>
        <Box sx={{ marginBottom: "5px" }}>
          <Typography variant="h2" color="black_opacity.100">{label}</Typography>
        </Box>
        <Divider />
      </Box>
      <Box sx={{ display: "flex", marginBottom: "5px" }}>
        <Box>
          <Box className="Carrier-modal-input-row">
            <PrimaryTextField
              label="Name*"
              value={name}
              onChange={setName}
              width="334px"
              placeholder="Contact Name"
              error={!name}
            />
          </Box>
          <Box className="Carrier-modal-input-row">
            {!contactTypeValid && (
              <Typography variant="body1" color="error.60">
                *Must include phone or email
              </Typography>
            )}
            <Box sx={{ ...(!contactTypeValid ? { marginLeft: "100px" } : { marginLeft: "270px" }) }}>
              <Typography variant="body1" color="black_solid.60">
                Primary
              </Typography>
              <Typography variant="body1" color="black_solid.60">
                Phone
              </Typography>
            </Box>
          </Box>
          <Box className="Carrier-modal-input-row">
            <Box>
              <PrimaryTextField
                label="Phone*"
                value={phone}
                onChange={setPhone}
                placeholder="123-456-7890"
                error={!contactTypeValid}
                isPhone
              />
            </Box>
            <Box sx={{ marginLeft: "16px" }}>
              <PrimaryTextField
                label=""
                width="60px"
                value={phoneExt}
                onChange={setPhoneExt}
                placeholder="Ext."
                isNumber
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <StandAloneRadio
                checked={primaryPhone === "P"}
                value="P"
                onChange={handlePrimaryPhoneChange}
                disabled={disablePrimaryPhone}
              />
            </Box>
          </Box>
          <Box className="Carrier-modal-input-row">
            <Box>
              <PrimaryTextField
                label="Mobile*"
                value={mobile}
                onChange={setMobile}
                placeholder="123-456-7890"
                error={!contactTypeValid}
                isPhone
              />
            </Box>
            <Box sx={{ marginLeft: "16px" }}>
              <PrimaryTextField
                label=""
                width="60px"
                value={mobileExt}
                onChange={setMobileExt}
                placeholder="Ext."
                isNumber
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <StandAloneRadio
                checked={primaryPhone === "M"}
                value="M"
                onChange={handlePrimaryPhoneChange}
                disabled={disablePrimaryPhone}
              />
            </Box>
          </Box>
          <Box className="Carrier-modal-input-row">
            <PrimaryTextField
              label="Email*"
              value={email}
              onChange={setEmail}
              width="334px"
              placeholder="Email"
              error={errorEmailValid()}
              setError={setEmailValid}
              isEmail
            />
          </Box>
          <Box className="Carrier-modal-input-row">
            <PrimaryTextField
              label="Fax"
              value={fax}
              onChange={setFax}
              width="334px"
              placeholder="123-456-7890"
              isPhone
            />
          </Box>
        </Box>
        <Box sx={{ marginLeft: "30px" }}>
          <FormGroup>
            {roles.map((role) => (
              <FormControlLabel
                key={`checkbox-${role.value}`}
                control={
                  <Box sx={{ margin: "6px" }}>
                    <PrimaryCheckbox
                      checked={role.state}
                      handleChange={handleRoleSelect}
                      value={role.value}
                      color="black_solid.60"
                    />
                  </Box>
                }
                label={
                  <Typography variant="h4" color="black_solid.80">
                    {role.label}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Box>
      </Box>
      <Box sx={{ display: "flex", width: "550px" }}>
        <Box sx={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
          <SecondaryButton
            label="Cancel"
            onClick={handleCancel}
            disabled={isUpdating}
          />
          <Box sx={{ marginLeft: "20px" }}>
            <PrimaryButton
              label="Submit"
              onClick={handleSubmit}
              isLoading={isUpdating}
              disabled={submitDisabled}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

CarrierContactsModal.propTypes = {};